import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowLeft } from "lucide-react";
import { Navigation, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuilderBuyBox() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    target_markets: "",
    min_acres: "",
    max_acres: "",
    max_price: "",
    property_types: "",
    utilities_required: "",
    close_timeline: "",
    notes: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      await axios.post(`${API}/leads/builder-buybox`, formData);
      setSubmitted(true);
      toast.success("Buy box submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="buybox-success">
        <Navigation mode="builder" />
        <div className="pt-24 pb-16 px-6 min-h-screen flex items-center justify-center">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-[#0F0F10] rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
              Buy Box Received
            </h1>
            <p className="text-[#57534E] text-lg mb-8">
              We'll match opportunities to your criteria. Thank you for your interest.
            </p>
            <Link to="/builder">
              <Button variant="outline" className="font-medium">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Builder Mode
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="buybox-page">
      <SEO 
        title="Submit Buy Box Criteria"
        description="Submit your land buying criteria to Steward Lots. Tell us what you're looking for and we'll match you with opportunities."
        path="/builder/buybox"
      />
      <Navigation mode="builder" />

      <div className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <Link to="/builder" className="text-[#57534E] hover:text-[#1C1917] inline-flex items-center gap-2 mb-6">
              <ArrowLeft className="w-4 h-4" />
              Back to Builder Mode
            </Link>
            <h1 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
              Submit Your Buy Box
            </h1>
            <p className="text-[#57534E] text-lg">
              Define your buying criteria and we'll match you with opportunities.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="buybox-form">
            {/* Contact Info */}
            <div className="space-y-6">
              <h2 className="font-serif text-xl text-[#1C1917] pb-4 border-b border-[#E7E5E4]">
                Contact Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Optional"
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Buy Box Criteria */}
            <div className="space-y-6">
              <h2 className="font-serif text-xl text-[#1C1917] pb-4 border-b border-[#E7E5E4]">
                Buy Box Criteria
              </h2>

              <div>
                <Label htmlFor="target_markets">Target Markets / Counties *</Label>
                <Textarea
                  id="target_markets"
                  name="target_markets"
                  value={formData.target_markets}
                  onChange={handleChange}
                  placeholder="List the markets, counties, or regions you're interested in"
                  rows={3}
                  required
                  data-testid="input-markets"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="min_acres">Min Acres</Label>
                  <Input
                    id="min_acres"
                    name="min_acres"
                    value={formData.min_acres}
                    onChange={handleChange}
                    placeholder="e.g., 5"
                    data-testid="input-min-acres"
                  />
                </div>
                <div>
                  <Label htmlFor="max_acres">Max Acres</Label>
                  <Input
                    id="max_acres"
                    name="max_acres"
                    value={formData.max_acres}
                    onChange={handleChange}
                    placeholder="e.g., 100"
                    data-testid="input-max-acres"
                  />
                </div>
                <div>
                  <Label htmlFor="max_price">Max Price</Label>
                  <Input
                    id="max_price"
                    name="max_price"
                    value={formData.max_price}
                    onChange={handleChange}
                    placeholder="e.g., $500,000"
                    data-testid="input-max-price"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="property_types">Property Types</Label>
                <Input
                  id="property_types"
                  name="property_types"
                  value={formData.property_types}
                  onChange={handleChange}
                  placeholder="e.g., Residential lots, commercial land, agricultural"
                  data-testid="input-types"
                />
              </div>

              <div>
                <Label htmlFor="utilities_required">Utilities Required</Label>
                <Input
                  id="utilities_required"
                  name="utilities_required"
                  value={formData.utilities_required}
                  onChange={handleChange}
                  placeholder="e.g., Water and electric at street"
                  data-testid="input-utilities"
                />
              </div>

              <div>
                <Label htmlFor="close_timeline">Typical Close Timeline</Label>
                <Input
                  id="close_timeline"
                  name="close_timeline"
                  value={formData.close_timeline}
                  onChange={handleChange}
                  placeholder="e.g., 30-45 days"
                  data-testid="input-timeline"
                />
              </div>

              <div>
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any other criteria or preferences"
                  rows={3}
                  data-testid="input-notes"
                />
              </div>
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              size="lg" 
              className="w-full bg-[#0F0F10] hover:bg-[#1C1917]"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? "Submitting..." : "Submit Buy Box"}
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
